/* Phänotypen Section – Sacred Modern Design
 * Dark section with DNA/Mandala fusion image
 * Explains the 12 phenotypes concept
 * Genetics + Culture + Environment = Why AYU is needed
 */
import { IMAGES } from "@/lib/constants";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Dna, Globe, Users } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const factors = [
  { icon: Dna, titleKey: "landing.phenotyp.genetikTitle", descriptionKey: "landing.phenotyp.genetikDesc" },
  { icon: Globe, titleKey: "landing.phenotyp.kulturTitle", descriptionKey: "landing.phenotyp.kulturDesc" },
  { icon: Users, titleKey: "landing.phenotyp.umweltTitle", descriptionKey: "landing.phenotyp.umweltDesc" },
];

export default function PhenotypSection() {
  const { t } = useI18n();
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="phaenotypen" className="section-dark py-24 lg:py-32 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 mandala-overlay pointer-events-none" />

      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: DNA Image */}
          <div
            className={`transition-all duration-700 ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            }`}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-[oklch(0.78_0.11_85/0.2)]">
              <img
                src={IMAGES.dna}
                alt="DNA trifft Mandala – Tradition und Wissenschaft vereint"
                className="w-full h-auto object-cover aspect-[16/10]"
              />
            </div>
          </div>

          {/* Right: Content */}
          <div
            className={`transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            }`}
          >
            <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)]" style={{ fontFamily: "var(--font-body)" }}>
              {t("landing.phenotyp.badge")}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.93_0.015_80)] mt-4 mb-6">
              {t("landing.phenotyp.heading1")} <span className="text-[oklch(0.52_0.09_128)]">{t("landing.phenotyp.heading2")}</span>
            </h2>

            <p className="text-lg text-[oklch(0.7_0.02_80)] leading-relaxed mb-8" style={{ fontFamily: "var(--font-body)" }}>
              {t("landing.phenotyp.intro")}
            </p>

            {/* Three Factors */}
            <div className="space-y-6">
              {factors.map((factor, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-xl bg-[oklch(0.52_0.09_128/0.15)] flex items-center justify-center shrink-0">
                    <factor.icon className="text-[oklch(0.52_0.09_128)]" size={22} />
                  </div>
                  <div>
                    <h4 className="font-bold text-[oklch(0.9_0.01_80)] mb-1">{t(factor.titleKey as any)}</h4>
                    <p className="text-[oklch(0.65_0.02_80)] text-sm" style={{ fontFamily: "var(--font-body)" }}>
                      {t(factor.descriptionKey as any)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Provokantes Statement */}
            <div className="mt-10 p-6 rounded-xl bg-[oklch(0.52_0.09_128/0.1)] border border-[oklch(0.78_0.11_85/0.2)]">
              <p className="text-lg italic text-[oklch(0.85_0.01_80)]" style={{ fontFamily: "var(--font-display)" }}>
                {t("landing.phenotyp.statement")}
              </p>
              <p className="text-sm text-[oklch(0.6_0.02_80)] mt-3" style={{ fontFamily: "var(--font-body)" }}>
                {t("landing.phenotyp.statementDesc")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
